import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';

interface HelpResource {
    icon: string;
    label: string;
    description: string;
    action: () => void;
}

const HelpWidget: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const [contextHelp, setContextHelp] = useState<HelpResource[]>([]);

    const toggle = () => setIsOpen(!isOpen);

    // Context-aware help based on current page
    useEffect(() => {
        const path = location.pathname;
        const helpResources: HelpResource[] = [];

        // Context-specific help
        if (path.includes('/endpoints')) {
            helpResources.push({
                icon: 'ri-book-open-line',
                label: 'Endpoint Setup Guide',
                description: 'Learn how to create and manage endpoints',
                action: () => window.open('https://integr.dev/documentation#step1', '_blank')
            });
        } else if (path.includes('/jobs')) {
            helpResources.push({
                icon: 'ri-book-open-line',
                label: 'API Integration Guide',
                description: 'Learn how to call your endpoints',
                action: () => window.open('https://integr.dev/documentation#step4', '_blank')
            });
        } else if (path.includes('/api-keys')) {
            helpResources.push({
                icon: 'ri-book-open-line',
                label: 'Getting Started',
                description: 'Complete integration guide',
                action: () => window.open('https://integr.dev/documentation', '_blank')
            });
        } else if (path.includes('/dashboard')) {
            helpResources.push({
                icon: 'ri-book-open-line',
                label: 'Documentation',
                description: 'Complete guide to using Integr.dev',
                action: () => window.open('https://integr.dev/documentation', '_blank')
            });
        }

        setContextHelp(helpResources);
    }, [location.pathname]);

    const commonResources: HelpResource[] = [
        {
            icon: 'ri-file-text-line',
            label: 'Documentation',
            description: 'Complete guides and tutorials',
            action: () => window.open('https://integr.dev/documentation', '_blank')
        },
        {
            icon: 'ri-code-line',
            label: 'API Reference',
            description: 'API endpoints and code examples',
            action: () => window.open('https://integr.dev/api-reference', '_blank')
        },
        {
            icon: 'ri-map-marker-path',
            label: 'Getting Started Tour',
            description: 'Interactive product walkthrough',
            action: () => {
                if ((window as any).restartOnboardingTour) {
                    (window as any).restartOnboardingTour();
                }
                setIsOpen(false);
            }
        },
        {
            icon: 'ri-customer-service-2-line',
            label: 'Contact Support',
            description: 'Get help from our team',
            action: () => window.location.href = '/contact-us'
        }
    ];

    const allResources = [...contextHelp, ...commonResources];

    return (
        <div className="help-widget-container">
            <Dropdown isOpen={isOpen} toggle={toggle} direction="up">
                <DropdownToggle
                    tag="button"
                    className="btn btn-primary btn-icon btn-lg rounded-circle help-widget-button"
                    title="Need help?"
                >
                    <i className="ri-question-line fs-20"></i>
                </DropdownToggle>
                <DropdownMenu className="help-widget-menu dropdown-menu-end" style={{ minWidth: '320px', maxHeight: '500px', overflowY: 'auto' }}>
                    <div className="p-3 border-bottom">
                        <h6 className="mb-1">How can we help?</h6>
                        <p className="text-muted mb-0 small">Quick access to resources</p>
                    </div>

                    {contextHelp.length > 0 && (
                        <>
                            <div className="px-3 py-2 bg-light">
                                <small className="text-muted fw-semibold">HELP FOR THIS PAGE</small>
                            </div>
                            {contextHelp.map((resource, index) => (
                                <DropdownItem
                                    key={`context-${index}`}
                                    onClick={resource.action}
                                    className="py-2"
                                >
                                    <div className="d-flex align-items-start">
                                        <div className="flex-shrink-0">
                                            <i className={`${resource.icon} fs-18 text-primary`}></i>
                                        </div>
                                        <div className="flex-grow-1 ms-3">
                                            <h6 className="mb-0 fs-14">{resource.label}</h6>
                                            <p className="text-muted mb-0 small">{resource.description}</p>
                                        </div>
                                    </div>
                                </DropdownItem>
                            ))}
                            <div className="dropdown-divider"></div>
                        </>
                    )}

                    <div className="px-3 py-2 bg-light">
                        <small className="text-muted fw-semibold">GENERAL RESOURCES</small>
                    </div>

                    {commonResources.map((resource, index) => (
                        <DropdownItem
                            key={`common-${index}`}
                            onClick={resource.action}
                            className="py-2"
                        >
                            <div className="d-flex align-items-start">
                                <div className="flex-shrink-0">
                                    <i className={`${resource.icon} fs-18 text-primary`}></i>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="mb-0 fs-14">{resource.label}</h6>
                                    <p className="text-muted mb-0 small">{resource.description}</p>
                                </div>
                            </div>
                        </DropdownItem>
                    ))}

                    <div className="dropdown-divider"></div>
                    <div className="p-3 text-center bg-light">
                        <small className="text-muted">
                            Need more help? <a href="/contact-us" className="fw-semibold">Contact us</a>
                        </small>
                    </div>
                </DropdownMenu>
            </Dropdown>
        </div>
    );
};

export default HelpWidget;
